import React, { useState } from 'react';
import { Table, Button, Typography, InputNumber, message } from 'antd';
import { DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface FollowUpItem {
  key?: string;
  departmentLabel: string;
  department_id?: number;
  product_category: string;
  product_category_id?: number;
  product?: string;
  product_id?: number;
  product_variety?: string;
  product_variety_id?: number;
  productLabel: string;
  qty: number;
  no_of_qa: number;
  uom: string;
  days?: number;
  layer: 'category' | 'species' | 'variety';
  unit_price?: number;
  serviceCharge?: string;
  service_category?: string;
  lcl?: number;
  fcl?: number;
  total?: number;
}

interface FollowUpItemTableProps {
  data: FollowUpItem[];
  onRemove?: (key: string) => void;
  onFieldUpdate?: (key: string, field: 'no_of_qa' | 'days', newValue: number) => Promise<void>;
  editable?: boolean;
  type?: 'edit' | 'create' | 'view';
  handleFieldChange?: (key: string, field: 'lcl' | 'fcl', value: number) => void;
}

const FollowUpItemTable: React.FC<FollowUpItemTableProps> = ({
  data,
  onRemove,
  onFieldUpdate,
  editable,
  type,
  handleFieldChange,
}) => {
  const [editingField, setEditingField] = useState<{ key: string; field: 'no_of_qa' | 'days' } | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [loading, setLoading] = useState<string | null>(null);

  const dataWithKeys = data.map((item, index) => ({
    ...item,
    key: item.key || `item-${index}`,
  }));

  const harbourItems = dataWithKeys.filter((item) => item.department_id === 7);
  const airportItems = dataWithKeys.filter((item) => item.department_id === 6);

  const handleEditStart = (key: string, field: 'no_of_qa' | 'days', currentValue: number) => {
    setEditingField({ key, field });
    setEditValue(currentValue);
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue(0);
  };

  const handleEditSave = async () => {
    if (!editingField || !onFieldUpdate) return;

    if (editValue <= 0) {
      message.error('Value must be greater than 0');
      return;
    }

    try {
      setLoading(editingField.key);
      await onFieldUpdate(editingField.key, editingField.field, editValue);
      setEditingField(null);
      setEditValue(0);
      message.success('Updated successfully');
    } catch (error) {
      message.error('Failed to update. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const renderEditableField = (value: number, record: FollowUpItem, field: 'no_of_qa' | 'days') => {
    const isEditing = editingField?.key === record.key && editingField?.field === field;
    const isLoading = loading === record.key;

    if (editable && type !== 'view') {
      if (isEditing) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <InputNumber
              min={1}
              value={editValue}
              onChange={(newValue) => setEditValue(newValue || 0)}
              style={{ width: 80 }}
              size="small"
              disabled={isLoading}
            />
            <Button
              type="text"
              icon={<CheckOutlined />}
              size="small"
              onClick={handleEditSave}
              loading={isLoading}
              style={{ color: '#52c41a' }}
            />
            <Button
              type="text"
              icon={<CloseOutlined />}
              size="small"
              onClick={handleEditCancel}
              disabled={isLoading}
              style={{ color: '#ff4d4f' }}
            />
          </div>
        );
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>{value}</span>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditStart(record.key!, field, value)}
            style={{ color: '#1890ff' }}
          />
        </div>
      );
    }

    return value;
  };

  const productColumns = [
    {
      title: 'Department',
      dataIndex: 'departmentLabel',
      width: '15%',
    },
    {
      title: 'Category',
      dataIndex: 'product_category',
      render: (_: any, record: FollowUpItem) => record.product_category || '-',
    },
    {
      title: 'Species',
      dataIndex: 'product',
      render: (_: any, record: FollowUpItem) => record.product || '-',
    },
    {
      title: 'Variety',
      dataIndex: 'product_variety',
      render: (_: any, record: FollowUpItem) => record.product_variety || '-',
    },
    {
      title: 'Days',
      dataIndex: 'days',
      render: (value: number, record: FollowUpItem) => renderEditableField(value, record, 'days'),
    },
    {
      title: 'Animal Count',
      dataIndex: 'no_of_qa',
      render: (value: number, record: FollowUpItem) => renderEditableField(value, record, 'no_of_qa'),
    },
    {
      title: 'Quantity',
      dataIndex: 'qty',
      render: (value: number, record: FollowUpItem) => {
        const isCurrentlyLoading = loading === record.key;
        return isCurrentlyLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>Updating...</span>
          </div>
        ) : (
          value
        );
      },
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      render: (value: string, record: FollowUpItem) => {
        const isCurrentlyLoading = loading === record.key;
        return isCurrentlyLoading ? 'Updating...' : value;
      },
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      render: (value: number, record: FollowUpItem) => {
        const isCurrentlyLoading = loading === record.key;
        if (isCurrentlyLoading) return 'Updating...';
        return value ? `LKR ${value}/=` : '-';
      },
    },
    {
      title: 'Subtotal',
      dataIndex: 'total',
      render: (_: any, record: FollowUpItem) => {
        const isCurrentlyLoading = loading === record.key;
        if (isCurrentlyLoading) return 'Updating...';

        const subtotal = record.total && record.total > 0 ? record.total : (record.qty || 1) * (record.unit_price || 0);

        return `LKR ${subtotal.toLocaleString()}/=`;
      },
    },
    ...(editable
      ? [
          {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: any, record: any) =>
              record.isHeader ? (
                { props: { colSpan: 0 } }
              ) : (
                <Button danger icon={<DeleteOutlined />} size="small" onClick={() => onRemove?.(record.key)} />
              ),
          },
        ]
      : []),
  ];

  const serviceColumns = [
    {
      title: 'Department',
      dataIndex: 'departmentLabel',
      width: '15%',
    },
    {
      title: 'Category',
      dataIndex: 'product_category',
      render: (_: any, record: FollowUpItem) => record.product_category || '-',
    },
    {
      title: 'Service Charge',
      dataIndex: 'serviceCharge',
      render: (value: string) => value ?? '-',
    },
    {
      title: 'LCL',
      dataIndex: 'lcl',
      render: (value: number, record: FollowUpItem) => {
        if (editable && type !== 'view') {
          return (
            <InputNumber
              min={0}
              value={record.lcl ?? 0}
              onChange={(val) => {
                handleFieldChange && handleFieldChange(record.key!, 'lcl', Number(val) || 0);
              }}
              style={{ width: 80 }}
              size="small"
            />
          );
        }
        return value || '-';
      },
    },
    {
      title: 'FCL',
      dataIndex: 'fcl',
      render: (value: number, record: FollowUpItem) => {
        if (editable && type !== 'view') {
          return (
            <InputNumber
              min={0}
              value={record.fcl ?? 0}
              onChange={(val) => {
                handleFieldChange && handleFieldChange(record.key!, 'fcl', Number(val) || 0);
              }}
              style={{ width: 80 }}
              size="small"
            />
          );
        }
        return value || '-';
      },
    },
    {
      title: 'Animal QTY',
      dataIndex: 'no_of_qa',
      render: (value: number, record: FollowUpItem) => renderEditableField(value, record, 'no_of_qa'),
    },
    {
      title: 'Quantity',
      dataIndex: 'qty',
      render: (value: number, record: FollowUpItem) => {
        const isCurrentlyLoading = loading === record.key;
        return isCurrentlyLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>Updating...</span>
          </div>
        ) : (
          value
        );
      },
    },

    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      render: (value: number, record: FollowUpItem) => {
        const isCurrentlyLoading = loading === record.key;
        if (isCurrentlyLoading) return 'Updating...';
        return value ? `LKR ${value}/=` : '-';
      },
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      render: (_: any, record: FollowUpItem) => {
        const isCurrentlyLoading = loading === record.key;
        if (isCurrentlyLoading) return 'Updating...';

        // Use existing total if available (from API), otherwise calculate
        const subtotal =
          record.total && record.total > 0 ? record.total : (record.no_of_qa || 1) * (record.unit_price || 0);

        return `LKR ${subtotal.toLocaleString()}/=`;
      },
    },
    ...(editable
      ? [
          {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: any, record: any) =>
              record.isHeader ? (
                { props: { colSpan: 0 } }
              ) : (
                <Button danger icon={<DeleteOutlined />} size="small" onClick={() => onRemove?.(record.key)} />
              ),
          },
        ]
      : []),
  ];

  return (
    <>
      {harbourItems.length > 0 && (
        <>
          <h3>Products</h3>
          <Table
            dataSource={harbourItems}
            columns={productColumns}
            pagination={false}
            rowKey="key"
            size="small"
            bordered
            style={{ marginBottom: 32 }}
          />
        </>
      )}

      {airportItems.length > 0 && (
        <>
          <h3>Services</h3>
          <Table
            dataSource={airportItems}
            columns={serviceColumns}
            pagination={false}
            rowKey="key"
            size="small"
            bordered
          />
        </>
      )}
    </>
  );
};

export default FollowUpItemTable;
